import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { baseapi } from "../Environmentalurl";
import { Dialog, DialogContent } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  ApplicationType,
  date,
  entity_id,
  getGeolocation,
  location,
  locationGeo,
  Platform,
} from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function ChangePinPopup({ open, onClose }) {
  /* ------------------- REDUX & SESSION ------------------- */

  const mobile = sessionStorage.getItem("mobileNumber");
  const sessionId = sessionStorage.getItem("SessionID");
  const ip = useIPAddress();
  /* ------------------- PIN STATES ------------------- */
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    success: false,
    message: "",
  });

  /* ------------------- API DATA STATES ------------------- */
  const [maskedCard, setMaskedCard] = useState("");
  const [encKey, setEncKey] = useState("");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const rupayRefNo = useSelector((state) => state.auth.rupayRefNo);
  console.log("heyyrupayRefNo" + rupayRefNo);

  /* ------------------- VISIBILITY ------------------- */
  const [showPin, setShowPin] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  /* ------------------- RESET FIELDS WHEN POPUP OPENS/CLOSES -------------------
     Clears any previously typed PINs so a returning user starts fresh and can't
     see what was entered before. Runs whenever `open` toggles. */
  useEffect(() => {
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
    setShowPin({ current: false, new: false, confirm: false });
    setDialog({ open: false, success: false, message: "" });
    setShowSuccessDialog(false);
  }, [open]);

  if (!open)
    return (
      <>
        {/* ================= MAIN CHANGE PIN POPUP ================= */}
        {open && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backdropFilter: "blur(6px)",
              backgroundColor: isDark ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.45)",
              zIndex: theme.zIndex.modal,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <Box
              sx={{
                width: 450,
                bgcolor: isDark ? "#0f172a" : "#ffffff",
                borderRadius: 5,
                boxShadow: "0px 10px 40px rgba(0,0,0,0.3)",
                p: 3,
              }}
            >
              {/* HEADER */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <IconButton onClick={onClose}>←</IconButton>
                <Typography fontWeight={700}>Change RuPay PIN</Typography>
                <LockIcon sx={{ color: "#22c55e" }} />
              </Box>

              {/* KEEP YOUR PIN INPUTS HERE (unchanged) */}

              {/* BUTTONS */}
              <Box mt={4} display="flex" gap={2}>
                <Button fullWidth onClick={onClose}>
                  Cancel
                </Button>
                <Button fullWidth onClick={handleUpdatePin}>
                  Update PIN
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* ================= SUCCESS DIALOG ================= */}
        <Dialog
          open={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
            onClose();
          }}
        >
          <DialogContent sx={{ textAlign: "center", p: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: "#22c55e" }} />
            <Typography fontSize={18} fontWeight={700} mt={2}>
              PIN Changed Successfully
            </Typography>
            <Typography mt={1}>
              Your RuPay card PIN has been updated successfully.
            </Typography>

            <Button
              sx={{ mt: 3 }}
              variant="contained"
              onClick={() => {
                setShowSuccessDialog(false);
                onClose();
              }}
            >
              OK
            </Button>
          </DialogContent>
        </Dialog>
      </>
    );

  /* ======================================================
     UPDATE PIN FLOW
  ====================================================== */
  const handleUpdatePin = async () => {
    try {
      if (!rupayRefNo) return;
      /* STEP 1: GET CARD STATUS */
      const cardRes = await api.post(`${baseapi}/getcardstatus`, {
        ReqType: "Jan_Aadhaar_eWallet_GetCardStatus",
        Mobilenumber: mobile,
        SessionID: sessionId,
        ReferenceNumber: rupayRefNo,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      });

      console.log(cardRes.data);

      // ✅ FIX: axios wraps the response in `.data`, so use cardRes.data.EncKey / cardRes.data.MaskedCard
      setMaskedCard(cardRes.data.MaskedCard);
      setEncKey(cardRes.data.EncKey);

      /* STEP 2: HEX XOR – CURRENT PIN */
      const { data: hexOld } = await api.post(
        `${baseapi}/hexXorgenerator`,
        {
          rpin: currentPin,
          mobilenumber: mobile,
        },
        {
          responseType: "text",
        },
      );
      console.log("hexOld:", hexOld);

      /* STEP 3: ENCRYPT CURRENT PIN */
      // ✅ FIX 1 of 2: was cardRes.EncKey (undefined) → now cardRes.data.EncKey
      const { data: encOld } = await api.post(
        `${baseapi}/ewalletRupayEncryption3DES`,
        {
          ReqType: "Jan_Aadhaar_eWallet_RupayEncryption3DES",
          Mobilenumber: mobile,
          Enc_Key: cardRes.data.EncKey,
          Date: date,
          Hex_XOR_Value: hexOld,
          SessionID: sessionId,
          Platform,
          ApplicationType,
          EntityID: entity_id,
          GeoLocation: locationGeo,
          MachineID: ip,
        },
      );
      console.log("encOld:", encOld);

      /* STEP 4: HEX XOR – NEW PIN */
      const { data: hexNew } = await api.post(
        `${baseapi}/hexXorgenerator`,
        {
          rpin: confirmPin,
          mobilenumber: mobile,
        },
        {
          responseType: "text",
        },
      );

      /* STEP 5: ENCRYPT NEW PIN */
      // ✅ FIX 2 of 2: was cardRes.EncKey (undefined) → now cardRes.data.EncKey
      const { data: encNew } = await api.post(
        `${baseapi}/ewalletRupayEncryption3DES`,
        {
          ReqType: "Jan_Aadhaar_eWallet_RupayEncryption3DES",
          Mobilenumber: mobile,
          Enc_Key: cardRes.data.EncKey,
          Date: date,
          Hex_XOR_Value: hexNew,
          SessionID: sessionId,
          Platform,
          ApplicationType,
          EntityID: entity_id,
          GeoLocation: locationGeo,
          MachineID: ip,
        },
      );

      /* STEP 6: CHANGE PIN */
      const { data: finalRes } = await api.post(
        `${baseapi}/eWalletRuPayChangePIN`,
        {
          ReqType: "Jan_Aadhaar_eWallet_RuPay_Change_PIN",
          Mobilenumber: mobile,
          SessionID: sessionId,
          CardNumber: cardRes.data.MaskedCard,
          R_PIN: encOld.RPIN,
          New_PIN: encNew.RPIN,
          ReferenceNumber: rupayRefNo,
          Platform,
          ApplicationType,
          EntityID: entity_id,
          GeoLocation: locationGeo,
          MachineID: ip,
        },
      );

      if (finalRes.Response_Code === "5000") {
        setDialog({
          open: true,
          success: true,
          message: finalRes.Message || "PIN Changed successfully",
        });
      } else {
        setDialog({
          open: true,
          success: false,
          message: finalRes.Message || "Failed to change PIN",
        });
      }
    } catch (err) {
      console.error(err);
      setDialog({
        open: true,
        success: false,
        message: "Something went wrong. Please try again.",
      });
    }
  };

  {
    /* SUCCESS DIALOG */
  }

  /* ======================================================
     UI
  ====================================================== */
  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          backgroundColor: isDark ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.45)",
          zIndex: 2000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            width: 450,
            bgcolor: isDark ? "#0f172a" : "#ffffff",
            borderRadius: 5,
            boxShadow: isDark
              ? "0px 10px 40px rgba(0,0,0,0.7)"
              : "0px 10px 40px rgba(0,0,0,0.2)",
            p: 3,
          }}
        >
          {/* HEADER */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <IconButton onClick={onClose}>
              <span style={{ fontSize: 20 }}>←</span>
            </IconButton>

            <Typography
              fontSize={18}
              fontWeight={700}
              color={isDark ? "#e5e7eb" : "#111"}
            >
              Change RuPay PIN
            </Typography>

            <IconButton>
              <LockIcon sx={{ color: "#22c55e" }} />
            </IconButton>
          </Box>

          {/* SECURE SESSION */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                px: 2,
                py: 0.5,
                backgroundColor: isDark ? "#064e3b" : "#E6FBEA",
                borderRadius: 20,
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: 12,
                color: isDark ? "#34d399" : "#10B981",
                fontWeight: 600,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: isDark ? "#34d399" : "#10B981",
                }}
              />
              Secure Session Active
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            {[
              {
                key: "current",
                label: "Current RuPay PIN",
                value: currentPin,
                set: setCurrentPin,
              },
              {
                key: "new",
                label: "New RuPay PIN",
                value: newPin,
                set: setNewPin,
              },
              {
                key: "confirm",
                label: "Confirm New PIN",
                value: confirmPin,
                set: setConfirmPin,
              },
            ].map((item) => (
              <Box key={item.key} mb={2}>
                <Typography fontSize={14} sx={{ mb: 1 }}>
                  {item.label}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 2,
                    border: isDark ? "1px solid #34d399" : "1px solid #22c55e",
                    px: 2,
                    height: 45,
                    bgcolor: isDark ? "#020617" : "#ffffff",
                  }}
                >
                  <input
                    value={item.value}
                    onChange={(e) =>
                      item.set(e.target.value.replace(/\D/g, ""))
                    }
                    type={showPin[item.key] ? "text" : "password"}
                    maxLength={4}
                    autoComplete="off"
                    style={{
                      border: "none",
                      color: isDark ? "#e5e7eb" : "#111",
                      outline: "none",
                      flexGrow: 1,
                      letterSpacing: "8px",
                      fontSize: "20px",
                      background: "transparent",
                    }}
                  />

                  <IconButton
                    onClick={() =>
                      setShowPin((p) => ({ ...p, [item.key]: !p[item.key] }))
                    }
                  >
                    {showPin[item.key] ? (
                      <VisibilityOffIcon />
                    ) : (
                      <RemoveRedEyeIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          {/* BUTTONS */}
          <Box mt={4} display="flex" justifyContent="space-between">
            <Button
              sx={{
                flex: 1,
                mr: 2,
                borderRadius: 2,
                height: 45,
                textTransform: "none",
                backgroundColor: isDark ? "#10b981" : "#059669",
                "&:hover": { backgroundColor: isDark ? "#34d399" : "#047857" },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              sx={{
                flex: 1,
                borderRadius: 2,
                height: 45,
                textTransform: "none",
                backgroundColor: "#059669",
                color: "white",
                "&:hover": { backgroundColor: "#047857" },
              }}
              onClick={handleUpdatePin}
            >
              Update PIN
            </Button>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialog.open}
        onClose={() => {
          setDialog({ open: false, success: false, message: "" });
          if (dialog.success) onClose();
        }}
        sx={{
          zIndex: 3000,
          "& .MuiPaper-root": {
            borderRadius: 4,
            boxShadow: "0px 20px 60px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: "center",
            p: 4,
            minWidth: 340,
          }}
        >
          {/* ICON BADGE */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: dialog.success
                ? "linear-gradient(135deg, #22c55e, #16a34a)"
                : "linear-gradient(135deg, #ef4444, #dc2626)",
            }}
          >
            {dialog.success ? (
              <CheckCircleIcon sx={{ fontSize: 42, color: "#fff" }} />
            ) : (
              <LockIcon sx={{ fontSize: 42, color: "#fff" }} />
            )}
          </Box>

          {/* TITLE */}
          <Typography fontSize={20} fontWeight={700} sx={{ color: "#111827" }}>
            {dialog.success ? "PIN Updated" : "Action Failed"}
          </Typography>

          {/* MESSAGE */}
          <Typography
            fontSize={14}
            mt={1}
            sx={{ color: "#6b7280", lineHeight: 1.6 }}
          >
            {dialog.message}
          </Typography>

          {/* BUTTON */}
          <Button
            variant="contained"
            sx={{
              mt: 3,
              px: 4,
              py: 1.2,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: dialog.success ? "#16a34a" : "#dc2626",
              "&:hover": {
                backgroundColor: dialog.success ? "#15803d" : "#b91c1c",
              },
            }}
            onClick={() => {
              setDialog({ open: false, success: false, message: "" });
              if (dialog.success) onClose();
            }}
          >
            Okay
          </Button>
        </DialogContent>
      </Dialog>
      ;
    </>
  );
}